import { AxiosError } from "axios";

/** Narrows a 422 `detail` array entry to one carrying a string `msg`. */
function hasStringMsg(entry: unknown): entry is { msg: string } {
  return (
    typeof entry === "object" &&
    entry !== null &&
    typeof (entry as { msg?: unknown }).msg === "string"
  );
}

/**
 * Best-effort human-readable message from a failed Core API call.
 *
 * FastAPI returns `{ detail: string }` for handler-raised 4xx (e.g. a
 * `HTTPException(400, "…")` such as a duplicate `config_code`) and
 * `{ detail: [{ msg, loc }] }` for request-schema (422) errors. We surface that
 * text so the form shows *why* a save failed instead of a generic message
 * (see `src/api/AGENTS.md` — "Handle 400 (validation) for form display").
 *
 * Falls back to `fallback` when nothing usable is present (network error, 5xx,
 * opaque body).
 */
export function backendErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { detail?: unknown } | undefined;
    const detail = data?.detail;

    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }

    if (Array.isArray(detail)) {
      const messages = detail
        .map((entry) => (hasStringMsg(entry) ? entry.msg : ""))
        .filter(Boolean);
      if (messages.length > 0) {
        return messages.join("; ");
      }
    }
  }
  return fallback;
}

/**
 * Error text for onboarding job hooks (`useOnboardingImport`,
 * `useOnboardingValidation`, `useOnboardingMigrationRun`) that poll after a
 * mutation — prefers FastAPI `detail` over Axios' generic status message.
 */
export function onboardingJobHookError(
  mutationError: unknown,
  pollError: unknown,
): string | null {
  if (mutationError) {
    const fallback =
      mutationError instanceof Error
        ? mutationError.message
        : String(mutationError);
    return backendErrorMessage(mutationError, fallback);
  }
  if (pollError != null) {
    const fallback =
      pollError instanceof Error ? pollError.message : String(pollError);
    return backendErrorMessage(pollError, fallback);
  }
  return null;
}
